/**
 * CSV export of the full guest registry for caterers, coordinators, venues, etc.
 */

import {
  ADMIN_RSVP_LABEL,
  FORM_RSVP,
  GUEST_POOL,
  GUEST_SIDE,
  SITE_VISIBILITY,
  VERBAL_RSVP,
  normalizeTags,
} from './guestAllowlist';

function csvEscape(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function labelSite(v) {
  if (v === SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK) return 'Ceremony + reception + on-site';
  if (v === SITE_VISIBILITY.RECEPTION_ONLY) return 'Reception only';
  return 'Ceremony + reception (day)';
}

function labelForm(v) {
  if (v === FORM_RSVP.YES) return 'Yes';
  if (v === FORM_RSVP.NO) return 'No';
  return 'Pending';
}

function labelVerbal(v) {
  if (v === VERBAL_RSVP.YES) return 'Yes';
  if (v === VERBAL_RSVP.NO) return 'No';
  if (v === VERBAL_RSVP.TENTATIVE) return 'Tentative';
  return 'Unset';
}

function labelAdminRsvp(g) {
  const k = g.adminRsvpStatus;
  return (k && ADMIN_RSVP_LABEL[k]) || '';
}

function labelPool(p) {
  return (p ?? GUEST_POOL.INVITED) === GUEST_POOL.BACKUP ? 'Backup' : 'Invited';
}

function labelGuestSide(s) {
  if (s === GUEST_SIDE.BEN) return 'Ben';
  if (s === GUEST_SIDE.MEG) return 'Meg';
  if (s === GUEST_SIDE.JOINT) return 'Joint';
  return '';
}

/**
 * @param {object[]} guests normalized registry rows
 * @returns {string}
 */
export function buildVendorGuestExportCsv(guests) {
  const headers = [
    'nickname_first_name',
    'last_name',
    'email',
    'phone',
    'group_household',
    'is_group_lead',
    'plus_ones_count',
    'total_seats_this_invitation',
    'dietary',
    'accommodation',
    'coach_in',
    'coach_out',
    'address',
    'notes',
    'tags_labels',
    'form_rsvp',
    'verbal_rsvp',
    'admin_rsvp',
    'site_access',
    'pool',
    'guest_side',
    'guest_id',
  ];

  const lines = [headers.map(csvEscape).join(',')];

  for (const g of guests) {
    const plus = typeof g.plusOnes === 'number' ? g.plusOnes : 0;
    const tags = normalizeTags(g.tags);
    const seats = 1 + plus;
    lines.push(
      [
        g.firstName ?? '',
        g.lastName ?? '',
        g.email ?? '',
        g.phone ?? '',
        g.group ?? '',
        g.isGroupLead ? 'yes' : 'no',
        String(plus),
        String(seats),
        g.dietary ?? '',
        g.accommodation ?? '',
        g.coachIn === true ? 'yes' : '',
        g.coachOut ?? '',
        g.address ?? '',
        g.notes ?? '',
        tags.join('; '),
        labelForm(g.formRsvp),
        labelVerbal(g.verbalRsvp),
        labelAdminRsvp(g),
        labelSite(g.siteVisibility),
        labelPool(g.pool),
        labelGuestSide(g.guestSide),
        g.id ?? '',
      ]
        .map(csvEscape)
        .join(','),
    );
  }

  return lines.join('\n');
}

/** Download full registry as UTF-8 CSV (with BOM for Excel). */
export function downloadVendorGuestExport(guests) {
  const csv = buildVendorGuestExportCsv(guests);
  const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `wedding-guests-vendors-${new Date().toISOString().slice(0, 10)}.csv`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
